import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Keep a number inside a min/max range.
 */
function clamp(n, min, max) {
  return Math.max(min, Math.min(n, max));
}

export default function DraggableComponent({
  storageKey = "chat-pos",
  docked,
  defaultPos = { x: 1000, y: 500 },
  children,
  usePortal = true,
  portalContainer,
  handle = null,
  cancel = "input,textarea,button,a,[data-no-drag],.no-drag",
  dragThreshold = 5,
  onDragStart,
  onDrag,
  onDragEnd,
  setPositionRef = null,
}) {
  const debug = (...args) => console.log("[DraggableComponent]", ...args);

  // The actual DOM node that gets dragged
  const nodeRef = useRef(null);

  // Ref version of position so pointer events always have latest value
  const posRef = useRef(defaultPos);

  // Refs for callbacks so pointer listeners can use latest prop functions
  const onDragStartRef = useRef(onDragStart);
  const onDragRef = useRef(onDrag);
  const onDragEndRef = useRef(onDragEnd);

  const [isDragging, setIsDragging] = useState(false);

  /**
   * Position state for the draggable element.
   * It starts from localStorage if available, otherwise from defaultPos.
   */
  const [pos, setPos] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      const parsed = saved ? JSON.parse(saved) : defaultPos;

      debug("initial position from localStorage/defaultPos", {
        storageKey,
        saved,
        parsed,
      });

      posRef.current = parsed;
      return parsed;
    } catch (err) {
      debug("failed reading localStorage, using defaultPos", err);
      posRef.current = defaultPos;
      return defaultPos;
    }
  });

  useEffect(() => {
    onDragStartRef.current = onDragStart;
  }, [onDragStart]);

  useEffect(() => {
    onDragRef.current = onDrag;
  }, [onDrag]);

  useEffect(() => {
    onDragEndRef.current = onDragEnd;
  }, [onDragEnd]);

  /**
   * Keep ref in sync with state.
   *
   * Important:
   * We only persist position to localStorage while NOT docked.
   * When docked, the parent (AiAssistant) controls the robot position.
   *
   * This is important because if we keep saving while docked,
   * we can accidentally save temporary snap/fallback positions.
   */
  useEffect(() => {
    posRef.current = pos;

    debug("pos state changed", { pos, docked });

    if (docked) {
      debug("skipping localStorage save because docked = true");
      return;
    }

    try {
      localStorage.setItem(storageKey, JSON.stringify(pos));
      debug("saved pos to localStorage", { storageKey, pos });
    } catch (err) {
      debug("failed saving pos to localStorage", err);
    }
  }, [pos, storageKey, docked]);

  /**
   * Expose an imperative function to parent:
   * parent can call dragSetPosRef.current(nextPos)
   * and that moves this draggable element directly.
   */
  useEffect(() => {
    if (!setPositionRef) return;

    setPositionRef.current = (next) => {
      debug("parent requested setPos", next);

      setPos(next);
      posRef.current = next;

      // If docked, do NOT save dock position as floating position
      if (!docked) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(next));
          debug("parent-set position also saved to localStorage", next);
        } catch (err) {
          debug("failed saving parent-set position", err);
        }
      } else {
        debug("parent-set position not saved because docked = true");
      }
    };

    return () => {
      debug("clearing setPositionRef.current");
      setPositionRef.current = null;
    };
  }, [setPositionRef, storageKey, docked]);

  /**
   * Pointer-based dragging logic.
   * Handles:
   * - pointer down
   * - move
   * - release
   */
  useEffect(() => {
    const el = nodeRef.current;
    if (!el) {
      debug("pointer effect skipped because nodeRef.current is null");
      return;
    }

    debug("binding draggable pointer listeners");

    let pointerId = null;
    let dragging = false;
    let started = false;
    let captured = false;

    let startX = 0;
    let startY = 0;
    let offsetX = 0;
    let offsetY = 0;

    const endDrag = () => {
      setIsDragging(false);

      if (dragging) {
        debug("endDrag -> firing onDragEnd with posRef.current", posRef.current);
        onDragEndRef.current?.(posRef.current);
      }

      dragging = false;
      started = false;

      if (captured && pointerId != null) {
        try {
          el.releasePointerCapture?.(pointerId);
        } catch (err) {
          debug("releasePointerCapture failed", err);
        }
      }

      captured = false;
      pointerId = null;
    };

    const onPointerDown = (e) => {
      if (cancel && e.target.closest(cancel)) {
        debug("pointerdown ignored because target matched cancel selector");
        return;
      }

      if (handle && !e.target.closest(handle)) {
        debug("pointerdown ignored because target did not match handle selector");
        return;
      }

      const r = el.getBoundingClientRect();

      startX = e.clientX;
      startY = e.clientY;

      // offset from pointer to top-left corner of draggable
      offsetX = startX - r.left;
      offsetY = startY - r.top;

      pointerId = e.pointerId;
      dragging = false;
      started = false;
      captured = false;

      debug("pointerdown accepted", {
        startX,
        startY,
        offsetX,
        offsetY,
        currentRect: r,
        currentPos: posRef.current,
      });
    };

    const onPointerMove = (e) => {
      if (pointerId == null || e.pointerId !== pointerId) return;

      const dx = Math.abs(e.clientX - startX);
      const dy = Math.abs(e.clientY - startY);
      const movedEnough = dx > dragThreshold || dy > dragThreshold;

      if (!dragging && movedEnough) {
        dragging = true;
        setIsDragging(true);

        if (!captured) {
          try {
            el.setPointerCapture?.(pointerId);
            captured = true;
          } catch (err) {
            debug("setPointerCapture failed", err);
          }
        }

        if (!started) {
          started = true;
          debug("drag officially started");
          onDragStartRef.current?.(posRef.current);
        }
      }

      if (!dragging) return;

      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const r = el.getBoundingClientRect();

      const left = e.clientX - offsetX;
      const top = e.clientY - offsetY;

      const maxX = vw - r.width;
      const maxY = vh - r.height;

      const next = {
        x: clamp(left, 0, Math.max(0, maxX)),
        y: clamp(top, 0, Math.max(0, maxY)),
      };

      debug("pointermove -> next position", next);

      setPos(next);
      posRef.current = next;
      onDragRef.current?.(next);

      e.preventDefault();
    };

    const onPointerUp = (e) => {
      if (pointerId == null) return;
      if (e.pointerId !== pointerId) return;

      debug("pointerup");
      endDrag();
    };

    const onPointerCancel = () => {
      debug("pointercancel");
      endDrag();
    };

    el.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove, { passive: false });
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerCancel);

    return () => {
      debug("cleaning draggable pointer listeners");
      el.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerCancel);
    };
  }, [handle, cancel, dragThreshold]);

  /**
   * This wrapper is always fixed at (0,0),
   * and the visible position is controlled by CSS transform.
   */
  const content = (
    <div
      ref={nodeRef}
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        transform: `translate(${pos.x}px, ${pos.y}px)`,
        transition: isDragging ? "none" : "transform 0.45s ease",
        touchAction: "none",
        userSelect: "none",
        zIndex: 2147483647,
        cursor: "grab",
        willChange: "transform",
      }}
      aria-label="Draggable"
      role="dialog"
    >
      {children}
    </div>
  );

  if (!usePortal || typeof document === "undefined") return content;

  const target = portalContainer || document.body;
  return createPortal(content, target);
}